/* eslint-disable no-restricted-globals */
import React, { lazy, Suspense, useState, useEffect } from 'react';
import { PageReady } from '@bingads-webui/page-ready';
import type {
  PageReadyComponent,
  PageReadyComponentLoader,
  PageReadyFallbackComponent,
  WithPageReadyProps,
} from './types';

export interface WithPageReadyAsyncOptions {
  /** When true, this component loads eagerly (bypassing PageReady delay) during
   *  SSR hydration mode. Use for shell components rendered by SSR (navigation,
   *  global menu, sub-page menu) to avoid hydration layout shift. */
  eagerInHydration?: boolean;
}

/**
 * Set to true by the hydration entry point (hydrate.js) before calling
 * ReactDOM.hydrate(). Components created with { eagerInHydration: true }
 * will bypass the PageReady delay and load synchronously.
 */
let hydrationModeEnabled = false;
export function setHydrationMode(enabled: boolean) {
  hydrationModeEnabled = enabled;
}
export function isHydrationMode(): boolean {
  return hydrationModeEnabled;
}

// ── Eager-hydration load registry ────────────────────────────────────────────
// A { eagerInHydration: true } component starts its import() at module-eval time and renders its
// placeholder until that resolves. import() NEVER resolves synchronously — webpackMode:'eager' only
// guarantees the module sits in the initial chunk, not that the promise is already settled. So if
// ReactDOM.hydrate() runs before the microtask drains, React hydrates the PLACEHOLDER against the
// server's real markup. React 17 client-renders that mismatch silently instead of throwing, which
// leaves the original server nodes in the DOM with no fiber attached. Since React 17 delegates
// events at the root container and resolves handlers by walking up from event.target looking for a
// __reactFiber$ key, those orphaned nodes drop every click while looking perfectly rendered.
//
// The hydration entry point therefore has to await these. ui-next's waitForHydrationLoaders() only
// covers lazyLoad()/registerHydrationFactory() feature-loader promises and never saw this set —
// that gap is what this registry closes.
const eagerHydrationLoads: Promise<void>[] = [];
const eagerHydrationFailures: string[] = [];

/**
 * Resolves once every { eagerInHydration: true } component's module has settled. Never rejects: a
 * component that fails to load must degrade to its placeholder, not wedge hydration for the app.
 *
 * @returns {Promise<void>} resolves when all eager-hydration modules have loaded or failed
 */
export function waitForEagerHydrationLoaders(): Promise<void> {
  return Promise.all(eagerHydrationLoads).then((): void => undefined);
}

/**
 * Eager-hydration components whose module failed to load outright. Empty is the healthy state;
 * anything here was hydrated as its placeholder because its chunk never arrived.
 *
 * @returns {string[]} placeholder names that rejected
 */
export function getFailedEagerHydrationComponents(): string[] {
  return eagerHydrationFailures.slice();
}

export function withPageReadyAsync<P extends object = {}>(
  componentLoader: PageReadyComponentLoader<P>,
  FallbackComponent: PageReadyFallbackComponent<P> = null,
  pageName: string = '',
  options: WithPageReadyAsyncOptions = {}
): React.MemoExoticComponent<React.ComponentType<WithPageReadyProps<P>>> {
  const { eagerInHydration = false } = options;

  // Hydration path: render the resolved component directly — no React.lazy, no Suspense — so a
  // suspending boundary can't throw away the server HTML during ReactDOM.hydrate().
  //
  // `import(/* webpackMode: 'eager' */ ...)` puts the module in the initial chunk, but it does NOT
  // make the promise settle synchronously; the component still renders its placeholder until the
  // microtask runs. The hydration entry point must therefore await waitForEagerHydrationLoaders()
  // before hydrating, or the placeholder is what gets hydrated against real server markup.
  if (eagerInHydration && hydrationModeEnabled) {
    let ResolvedComponent: PageReadyComponent<P> | null = null;
    let loadPromise: Promise<void> | null = null;

    // Names this component if its chunk fails to load. The loader is an inline arrow with no
    // inferred name, so the placeholder is the only human-readable handle available.
    const label = pageName ||
      (FallbackComponent && (FallbackComponent.displayName || FallbackComponent.name)) ||
      `eagerHydrationComponent#${eagerHydrationLoads.length}`;

    // Memoize a single load promise so the kickoff below and the effect below
    // attach to the SAME promise. Without this, the loader could resolve in the
    // window between a component's render returning and its passive effect
    // running — setting ResolvedComponent but firing no state update — leaving
    // the fallback rendered until an unrelated re-render. (Very likely for the
    // nav wrappers, which call withPageReadyAsync inside useMemo during render,
    // so the loader is always still pending at first render.)
    const ensureLoaded = () => {
      if (!loadPromise) {
        loadPromise = componentLoader().then((mod) => {
          ResolvedComponent = mod.default;
        });
      }
      return loadPromise;
    };

    // Kick off loading immediately to minimize hydration fallback time, and register it so the
    // hydration entry point can await it BEFORE ReactDOM.hydrate(). The registered copy swallows
    // rejection on purpose: a failed chunk must fall back to the placeholder, never block hydration
    // for the entire app.
    eagerHydrationLoads.push(ensureLoaded().catch(() => {
      eagerHydrationFailures.push(label);
    }));

    return React.memo((props: WithPageReadyProps<P>) => {
      const pageReadyProps = { ...props, pageReady: true } as P;
      const fallbackProps = { ...props, pageReady: false } as P;

      // ResolvedComponent may already be resolved synchronously (eager import).
      // If not yet resolved, re-render once it is.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [, forceUpdate] = useState(0);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        let cancelled = false;
        // Always attach to the shared promise, so we still re-render even if it
        // resolved between initial render and this effect running.
        ensureLoaded().then(() => {
          if (!cancelled) {
            forceUpdate((n: number) => n + 1);
          }
        });
        return () => { cancelled = true; };
      }, []);

      if (ResolvedComponent) {
        return React.createElement(ResolvedComponent, pageReadyProps);
      }
      return FallbackComponent ? React.createElement(FallbackComponent, fallbackProps) : null;
    });
  }

  const pageReadyPromise = PageReady.afterPageReady(pageName);
  const WrappedComponent = lazy(() => pageReadyPromise.then(() => componentLoader()));

  return React.memo((props: WithPageReadyProps<P>) => {
    const pageReadyProps = { ...props, pageReady: true } as P;
    const fallbackProps = { ...props, pageReady: false } as P;

    return (
      <Suspense fallback={FallbackComponent === null ? null : React.createElement(FallbackComponent, fallbackProps)}>
        {React.createElement(
          WrappedComponent as unknown as React.FunctionComponent<P>,
          pageReadyProps
        )}
      </Suspense>
    );
  });
}
